import { PLAY_TYPES } from '../rules/payouts';
import { formatCubaTimeLabel } from '../time/cubaTime';

export type PlayStatus = 'open' | 'won' | 'expired' | 'rejected' | 'annulled';

export type PlayManagementTrailEntry = {
  action: 'annul' | 'edit' | 'promote';
  reasonCode: string;
  reasonNote?: string;
  actorId: string;
  actorRole?: string;
  at: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
};

export type Play = {
  id: string;
  houseId: string;
  listerId: string;
  listerName: string;
  type: string;
  number: string;
  number2: string | null;
  stake: number;
  window: string;
  dayKey?: string;
  clientId?: string;
  /** Human receipt code for prize claim (XXXX-XXXX). */
  claimCode?: string;
  /** Optional buyer name / note captured at registration. */
  buyerNote?: string;
  /** Prize multiplier sealed at accept time. */
  payoutMultiplier?: number;
  status: PlayStatus | string;
  rejectKey?: string;
  createdAt: number;
  /** Legacy display time from mock data */
  t?: string;
  /** Legacy number field from mock data */
  n?: string;
  payout?: number;
  /** Epoch ms when the winning prize was cashed. */
  paidAt?: number;
  paidBy?: string;
  paidByName?: string;
  paidClaimCode?: string;
  /** Append-only trail of management actions (annul / adjust / revise). */
  management?: PlayManagementTrailEntry[];
};

export function createPlay(fields: Partial<Play> = {}): Play {
  return {
    id:          fields.id ?? crypto.randomUUID(),
    houseId:     fields.houseId   ?? '',
    listerId:    fields.listerId   ?? '',
    listerName:  fields.listerName ?? '',
    type:        fields.type       ?? PLAY_TYPES.FIJO,
    number:      fields.number     ?? '',
    number2:     fields.number2    ?? null,
    stake:       Number(fields.stake ?? 0),
    window:      fields.window     ?? 'day',
    dayKey:      fields.dayKey     ?? undefined,
    clientId:    fields.clientId   ?? undefined,
    claimCode:   fields.claimCode  ?? undefined,
    buyerNote:   fields.buyerNote  ?? undefined,
    payoutMultiplier: fields.payoutMultiplier ?? undefined,
    status:      fields.status     ?? 'open',
    paidAt:      fields.paidAt     ?? undefined,
    paidBy:      fields.paidBy     ?? undefined,
    paidByName:  fields.paidByName ?? undefined,
    paidClaimCode: fields.paidClaimCode ?? undefined,
    rejectKey:   fields.rejectKey  ?? undefined,
    createdAt:   fields.createdAt  ?? Date.now(),
  };
}

export function playNumberDisplay(play: {
  type?: string;
  number?: string;
  number2?: string | null;
  n?: string;
}): string {
  const number = play.number ?? play.n ?? '';
  const number2 = play.number2 ?? null;
  if (play.type === PLAY_TYPES.PARLE && number2) {
    return `${number}·${number2}`;
  }
  return number;
}

/** Localized play type label for list rows. */
export function playTypeLabel(type: string, t: Record<string, string>) {
  return t[type] ?? type;
}

function resolveCreatedAtMs(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'object' && raw !== null) {
    const ts = raw as { toMillis?: () => number; seconds?: number };
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  }
  return null;
}

/** Display time for a play row (Cuba civil time). */
export function playTimeLabel(play: Pick<Play, 't' | 'createdAt'>): string {
  if (play.t) return play.t;
  const ms = resolveCreatedAtMs(play.createdAt);
  if (ms == null) return '';
  return formatCubaTimeLabel(new Date(ms));
}
